import React from 'react'
import { Link } from 'react-router-dom'
import { GoAlertFill } from 'react-icons/go'
import { FaPhoneAlt, FaUserCircle, FaCheckCircle } from 'react-icons/fa'
import { motion } from 'framer-motion'

interface VerificationStepsProps {
  hasPhone: boolean
  hasProfile?: boolean
  t: (key: string) => string
}

const VerificationSteps: React.FC<VerificationStepsProps> = ({
  hasPhone,
  hasProfile,
  t,
}) => {
  const baseSteps = [
    {
      step: 1,
      title: t('verification.phone-step'),
      completed: hasPhone,
      warning: t('announce-no-phone-warning'),
      buttonText: t('dashboard.account-settings'),
      icon: FaPhoneAlt,
      link: '/dashboard/settings',
    },
  ]

  const profileStep = {
    step: 2,
    title: t('verification.profile-step'),
    completed: hasProfile,
    warning: t('create-ad.announce-no-profile-warning'),
    buttonText: t('create-profile'),
    icon: FaUserCircle,
    link: '/dashboard/profile',
  }

  // N'ajoute le step 2 que si hasProfile est défini dans les props
  const steps =
    hasProfile !== undefined ? [...baseSteps, profileStep] : baseSteps

  return (
    <div className='w-full space-y-6 p-4'>
      {steps.map((step, index) => (
        <div key={step.step} className='flex flex-col sm:flex-row gap-4'>
          <div className='flex items-center justify-center sm:justify-start'>
            <div
              className={`rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center 
              ${
                step.completed
                  ? 'bg-emerald-500 text-white'
                  : 'bg-redpink text-white'
              }
              text-base sm:text-lg font-medium`}
            >
              {step.step}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className='flex-1'
          >
            <div className={`w-full rounded-lg overflow-hidden bg-anthracite`}>
              <div
                className={`flex items-center px-4 py-3 sm:py-4 border-redpink ${
                  step.completed ? '' : 'border-b-2'
                }`}
              >
                <step.icon
                  className={`text-xl sm:text-2xl ${
                    step.completed ? 'text-emerald-500' : 'text-redpink'
                  } mr-3`}
                />
                <h3 className='font-medium text-white text-sm sm:text-base'>
                  {step.title}
                </h3>
                {step.completed && (
                  <FaCheckCircle className='ml-auto text-emerald-500 text-lg sm:text-xl' />
                )}
              </div>

              {!step.completed && (
                <div className='p-3 sm:p-4 space-y-3 sm:space-y-4'>
                  <div className='flex items-start text-white text-xs sm:text-sm'>
                    <GoAlertFill className='text-redpink text-lg sm:text-xl flex-shrink-0 mr-2 mt-1' />
                    <p>{step.warning}</p>
                  </div>

                  <Link to={step.link} className='block w-full sm:w-auto'>
                    <button
                      className='w-full sm:w-auto px-4 sm:px-6 py-2 bg-redpink hover:bg-redpink/90
                      text-white rounded-lg transition-colors duration-200
                      flex items-center justify-center text-sm sm:text-base'
                    >
                      {step.buttonText}
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      ))}
    </div>
  )
}

export default VerificationSteps
